import { VendorPickerBC } from '@/components/filtros/VendorPickerBC';
import { Button } from '@/components/ui/button';
import { useSugerenciasProveedor, useVincularProveedorConcepto } from '@/hooks/useFlujo';
import { useVendoresBC } from '@/hooks/useVendoresBC';
import type { ConceptoSinVincular } from '@adelante/shared';
import { useState } from 'react';
import { toast } from 'sonner';

function TarjetaConcepto({ concepto }: { concepto: ConceptoSinVincular }) {
  const { mutate: vincular, isPending } = useVincularProveedorConcepto();
  const { data: vendoresBC } = useVendoresBC();
  const [buscandoManual, setBuscandoManual] = useState(false);
  const [oculto, setOculto] = useState(false);

  if (oculto) return null;

  const confirmar = (vendorCode: string, vendorName: string) => {
    vincular(
      { id: concepto.conceptoId, vendorCode, vendorName },
      {
        onSuccess: () => {
          toast.success(`"${concepto.conceptoNombre}" vinculado con ${vendorName}`);
          setOculto(true);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo vincular'),
      },
    );
  };

  const confirmarManual = (vendorCode: string | null) => {
    if (!vendorCode) return;
    const vendor = (vendoresBC?.data ?? []).find((v) => v.vendorCode === vendorCode);
    confirmar(vendorCode, vendor?.vendorName ?? vendorCode);
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-sm">{concepto.conceptoNombre}</p>
          <p className="text-xs text-muted-foreground">
            {concepto.categoriaNombre} · {concepto.moneda === 'C' ? 'Colones' : 'Dólares'}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="text-xs" onClick={() => setOculto(true)}>
          Omitir
        </Button>
      </div>

      {concepto.sugerencias.length === 0 && !buscandoManual && (
        <p className="text-sm text-muted-foreground">Sin candidatos por similitud de nombre.</p>
      )}

      <div className="flex flex-wrap gap-2">
        {concepto.sugerencias.map((s) => (
          <Button
            key={s.vendorCode}
            type="button"
            variant="outline"
            size="sm"
            disabled={isPending}
            className="text-xs h-auto py-1.5"
            onClick={() => confirmar(s.vendorCode, s.vendorName)}
          >
            {s.vendorName}{' '}
            <span className="text-muted-foreground ml-1">
              ({s.vendorCode} · {Math.round(s.score * 100)}%)
            </span>
          </Button>
        ))}
      </div>

      {buscandoManual ? (
        <VendorPickerBC value={null} onChange={confirmarManual} />
      ) : (
        <Button
          variant="link"
          size="sm"
          className="text-xs h-auto p-0"
          onClick={() => setBuscandoManual(true)}
        >
          Ninguno coincide, buscar manualmente
        </Button>
      )}
    </div>
  );
}

export function VincularProveedoresPantalla() {
  const { data: conceptos, isLoading, error } = useSugerenciasProveedor();

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold">Vincular Proveedores Crédito con BC</h1>
        <p className="text-sm text-muted-foreground">
          Estos conceptos de "Proveedores Crédito" no están vinculados a un proveedor de Business
          Central — sin ese vínculo, las propuestas de pago no pueden reflejarse acá. Confirmá el
          candidato correcto o buscá manualmente.
        </p>
      </div>

      {isLoading && <div className="text-muted-foreground text-sm">Buscando candidatos...</div>}
      {error && (
        <div className="text-red-600 text-sm">No se pudo cargar: {(error as Error).message}</div>
      )}
      {conceptos && conceptos.length === 0 && (
        <div className="text-muted-foreground text-sm">
          Todos los conceptos de Proveedores Crédito ya están vinculados. 🎉
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {conceptos?.map((c) => (
          <TarjetaConcepto key={c.conceptoId} concepto={c} />
        ))}
      </div>
    </div>
  );
}
